import * as Dialog from '@radix-ui/react-dialog';
import { X, TriangleAlert, CheckCircle2, Info, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { cn } from './cn';
import { Button } from './Button';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Sticky footer (e.g. Cancel / Save buttons). */
  footer?: ReactNode;
  /** Max width in px (caps to viewport). */
  width?: number;
  className?: string;
  /** antd `maskClosable={false}` — a backdrop click does NOT close the dialog (guards forms). */
  maskClosable?: boolean;
  /** antd `keyboard={false}` — Esc does NOT close the dialog. */
  keyboard?: boolean;
}

/**
 * Centered dialog (replaces antd Modal) built on Radix Dialog — focus-trapped,
 * Esc/overlay close, accessible. Header + scrollable body + sticky footer; the
 * body scrolls while header/footer stay pinned.
 */
function ModalBase({ open, onOpenChange, title, description, children, footer, width = 560, className, maskClosable, keyboard }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-overlay backdrop-blur-[1px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[201] flex max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'w-[calc(100vw-2rem)] rounded-card border border-line bg-surface shadow-pop',
            'data-[state=open]:animate-pop-in',
            className,
          )}
          style={{ width: `min(${width}px, calc(100vw - 2rem))` }}
          // antd parity: maskClosable={false} → backdrop click is a no-op; keyboard={false} → Esc is a no-op.
          onPointerDownOutside={maskClosable === false ? (e) => e.preventDefault() : undefined}
          onInteractOutside={maskClosable === false ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={keyboard === false ? (e) => e.preventDefault() : undefined}
        >
          {/* Always render a Title for a11y; hide it visually when none is provided. */}
          <div className={cn(title != null || description != null ? 'flex items-start justify-between gap-4 border-b border-line px-5 py-4' : 'contents')}>
            <div className="min-w-0">
              <Dialog.Title className={cn(title != null ? 'text-base font-semibold text-content' : 'sr-only')}>
                {title ?? 'Dialog'}
              </Dialog.Title>
              {description != null && <Dialog.Description className="mt-0.5 text-[13px] text-muted">{description}</Dialog.Description>}
            </div>
            <Dialog.Close
              className={cn(
                'grid size-8 shrink-0 place-items-center rounded-control text-muted transition-colors hover:bg-surface-2 hover:text-content',
                title == null && description == null && 'absolute right-3 top-3 z-10',
              )}
              aria-label="Close"
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer != null && (
            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Imperative Modal.confirm / .info / .success / .error / .warning ───────────
interface ConfirmConfig {
  title?: ReactNode;
  content?: ReactNode;
  icon?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: 'primary' | 'danger';
  okButtonProps?: { danger?: boolean; loading?: boolean };
  onOk?: () => void | Promise<unknown>;
  onCancel?: () => void;
  width?: number;
  /** false → single OK button (used by info/success/error/warning). */
  okCancel?: boolean;
  type?: 'confirm' | 'info' | 'success' | 'error' | 'warning';
}

const TYPE_ICON: Record<string, ReactNode> = {
  confirm: <TriangleAlert className="text-warning-500" />,
  warning: <TriangleAlert className="text-warning-500" />,
  info: <Info className="text-sky-500" />,
  success: <CheckCircle2 className="text-brand-600" />,
  error: <XCircle className="text-danger-500" />,
};

function spawnConfirm(config: ConfirmConfig) {
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  const destroy = () => { setTimeout(() => { root.unmount(); host.remove(); }, 200); };

  function ConfirmDialog() {
    const [open, setOpen] = useState(true);
    const [busy, setBusy] = useState(false);
    const close = () => setOpen(false);
    const cancel = () => { close(); config.onCancel?.(); destroy(); };
    const ok = async () => {
      try { setBusy(true); await config.onOk?.(); close(); destroy(); }
      catch { setBusy(false); /* keep open on rejection, antd parity */ }
    };
    return (
      <ModalBase
        open={open}
        onOpenChange={(o) => { if (!o) cancel(); }}
        width={config.width ?? 440}
        footer={
          <>
            {config.okCancel !== false && <Button variant="default" onClick={cancel}>{config.cancelText ?? 'Cancel'}</Button>}
            <Button variant={config.okType === 'danger' || config.okButtonProps?.danger ? 'danger' : 'primary'} loading={busy || config.okButtonProps?.loading} onClick={ok}>
              {config.okText ?? 'OK'}
            </Button>
          </>
        }
      >
        <div className="flex gap-3.5">
          <span className="mt-0.5 shrink-0 [&>svg]:size-6">{config.icon ?? TYPE_ICON[config.type ?? 'confirm']}</span>
          <div className="min-w-0">
            {config.title != null && <div className="text-base font-semibold text-content">{config.title}</div>}
            {config.content != null && <div className="mt-1 text-sm leading-relaxed text-muted">{config.content}</div>}
          </div>
        </div>
      </ModalBase>
    );
  }
  root.render(<ConfirmDialog />);
  return { destroy };
}

export const Modal = Object.assign(ModalBase, {
  confirm: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'confirm' }),
  info: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'info', okCancel: false }),
  success: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'success', okCancel: false }),
  error: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'error', okCancel: false }),
  warning: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'warning', okCancel: false }),
});
